import { useProfile } from "@/context/useProfileProps";
import React, { useRef, useState } from "react";

import { UploadAvatarProps } from "../../types";
import Avatar from "./Avatar";
import { useUploadAvatar } from "./query";

const ALLOWED_FILE_TYPES = ["png", "jpg", "jpeg"] as const;
const MAX_FILE_SIZE_MB = 6;

const UploadAvatar = ({ avatar, nameInitial }: UploadAvatarProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate } = useUploadAvatar();

  const {
    isMobile,
    showToast,
    translation: { basic, errorMessages },
  } = useProfile();

  const validateFile = (file: File) => {
    // Check if it's actually an image file
    if (!file.type.startsWith("image/")) {
      return { isValid: false, error: basic.fileErr };
    }

    const fileSize = Number((file.size / 1000000).toFixed(2));
    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";

    const isFileSizeValid = fileSize <= MAX_FILE_SIZE_MB;
    const isFileTypeValid = ALLOWED_FILE_TYPES.includes(
      fileExtension as (typeof ALLOWED_FILE_TYPES)[number],
    );

    if (!isFileSizeValid) {
      return {
        isValid: false,
        error: `File size should be less than ${MAX_FILE_SIZE_MB}MB`,
      };
    }

    if (!isFileTypeValid) {
      return {
        isValid: false,
        error: `Only ${ALLOWED_FILE_TYPES.join(", ")} files are allowed`,
      };
    }

    return { isValid: true, error: null };
  };

  const handleUploadSuccess = () => {
    setIsLoading(false);
    if (typeof window.MessageHandler?.postMessage === "function") {
      window.MessageHandler.postMessage(
        JSON.stringify({
          type: "PROFILE_AVATAR_UPDATED",
        }),
      );
    }
    showToast({
      show: true,
      message: "Profile pic updated successfully!",
      type: "success",
    });
  };

  const handleUploadError = (error: any) => {
    setIsLoading(false);
    showToast({
      show: true,
      message:
        error instanceof Error
          ? error.message
          : errorMessages.somethingWentWrong,
      type: "error",
    });
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // Always reset the input first to ensure it works on reselection
    resetFileInput();

    if (!file) return;

    const { isValid, error } = validateFile(file);
    if (!isValid) {
      showToast({
        show: true,
        type: "error",
        message: error || basic.fileErr,
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setIsLoading(true);

    mutate(
      { data: formData, action: "add" },
      {
        onSuccess: handleUploadSuccess,
        onError: handleUploadError,
      },
    );
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={`flex max-w-[100%] gap-4 rounded-md p-[10px] ${
        isMobile
          ? "flex-col items-center"
          : "bg-surface-secondary items-baseline"
      } ${isLoading && "animate-pulse"}`}
    >
      <Avatar avatar={avatar} nameInitial={nameInitial} />
      <span
        className={`text-brand-primary cursor-pointer font-semibold ${
          isMobile ? "text-xs" : "text-sm"
        }`}
        onClick={triggerFileInput}
      >
        {basic.changePhoto}
      </span>
      <input
        className="hidden"
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        name="avatar"
        onChange={handleFileUpload}
      />
    </div>
  );
};

export default UploadAvatar;
